import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from './lib/utils';

const Tabs = TabsPrimitive.Root;

/* ─── Underline Tabs (L1 — page-level navigation) ─── */

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'flex items-center gap-1 border-b border-nn-border',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center gap-1.5 whitespace-nowrap px-3 py-2',
      'text-[13px] font-medium transition-all',
      'border-b-2 border-transparent -mb-px',
      'text-text-dark-secondary hover:text-text-dark',
      'outline-none focus-visible:ring-2 focus-visible:ring-aqua',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:border-b-aqua data-[state=active]:text-aqua',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

/* ─── Folder Tabs (L2 — sub-tab navigation) ─── */

/**
 * FolderTabsList — container for folder-style tab triggers.
 * Renders above FolderTabsContent; active trigger merges into the content
 * panel via the `.folder-tab-trigger` CSS class (see index.css).
 */
const FolderTabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('flex items-end gap-0 relative z-10 pl-2', className)}
    {...props}
  />
));
FolderTabsList.displayName = 'FolderTabsList';

/**
 * FolderTabsTrigger — folder-style tab button.
 * Active state: 3-sided border with bottom border matching content panel
 * background, creating a seamless merge. Styled via `.folder-tab-trigger`
 * in index.css (the border-merge trick requires CSS variables that
 * Tailwind's data-attribute selectors cannot resolve).
 */
const FolderTabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'folder-tab-trigger',
      'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
      'px-3.5 py-2 text-[12px] font-medium transition-colors',
      'text-text-dark-secondary hover:text-text-dark',
      'outline-none focus-visible:ring-2 focus-visible:ring-aqua',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:text-aqua',
      className
    )}
    {...props}
  />
));
FolderTabsTrigger.displayName = 'FolderTabsTrigger';

/**
 * FolderTabsContent — bordered content panel that folder triggers merge into.
 */
const FolderTabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'border border-nn-border rounded-b-lg rounded-tr-lg bg-nn-white',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua',
      className
    )}
    {...props}
  />
));
FolderTabsContent.displayName = 'FolderTabsContent';

export {
  Tabs, TabsList, TabsTrigger, TabsContent,
  FolderTabsList, FolderTabsTrigger, FolderTabsContent,
};
